/* =============================================================================
   TIMED CLOSING — the hub and lot page's real-time half (2026-10-13)
   =============================================================================
   An Online Timed Close sale (events.auction_kind = 'online-timed-close') closes its
   lots one after another, run by the SERVER (lib/timed-close.js): a one-minute clock on
   lot 001, every bid that moves the price adds up to 15 seconds (never above a minute),
   and when it runs out the lot is SOLD or PASSED and the next one starts.

   ⚠️ ONE BIDDING STYLE, ALL ALONG (owner, 2026-10-25). A timed sale has no "absentee"
   phase. Every card, from the moment bidding opens to the moment its own lot closes,
   offers the same two things — and so does the bar:
     • "Bid $X"    — an exact bid at the next step;
     • "Set a max" — a maximum the system bids up to for you, one step at a time.
   Both go to POST /api/timed/bid ({ kind: 'bid' | 'max' }). The current high bidder is
   only ever offered "Raise max": their own bid never raises the price (the server
   enforces that too).

   This file is everything a viewer sees of that, logged in or not:
     • a docked NOW CLOSING bar — the lot, its clock, the price, who leads, and (for a
       bidder) Bid $X / Bid $Y / any amount / Set a max, tap then confirm inline, the way
       the live page's chat bid bar works. The same bar on every page of the hub and on
       the lot page, so a bidder three pages away can still bid.
     • every card's own Bid $X + Set a max (replacing the absentee controls), with
       "You're the high bidder" / "You've been outbid" / "Your top bid";
     • a red "+15s" every time the clock is pushed back;
     • the closing card boxed and highlighted; a SOLD / PASSED flash on each card as
       its clock runs out, then the card greyed with its result;
     • every lot's price, count and next ask as bids land (timed-lot-update).

   ⚠️ NOTHING HERE DECIDES ANYTHING. The clock, the asks (ONE ladder, served per lot),
   the outcome and the order of the lots are the server's; this draws what it is told
   and asks again on reconnect. The local clock is only an offset from the server's
   (serverNow on every message), so a phone whose clock is wrong still counts down to
   the right moment.

   ⚠️ RESERVES (2026-10-26): every lot payload carries `reserveMet` (true / false / null =
   no reserve — never the amount). The bar shows a "Reserve not met" / "Reserve met" pill
   under the price, every card's pill flips with it (mhv-reserve.js MHVReserve.set), and
   a lot that closes under its reserve flashes "Passed — reserve not met". Under the
   engine's reserve rule a leader raising their own max CAN move the price (to the new
   max, or to the reserve) — that comes back as `moved` and is drawn like any bid.

   ⚠️ A SALE THAT IS NOT TIMED COSTS ONE GET AND NOTHING ELSE — no socket, no bar.
   ============================================================================= */
(function () {
  'use strict';
  if (window.__mhvTimed) return;
  window.__mhvTimed = true;

  var CFG = window.MHV_TIMED_CONFIG || {};
  var CODE = String(CFG.sessionCode || '');
  if (!/^\d{8}$/.test(CODE)) return;
  var SERVER = (window.MHV && window.MHV.SERVER_URL) || 'https://mhv-viewer-app-1df0c0c61ed9.herokuapp.com';

  function hubCfg() { return window.MHV_AUCTION_HUB_CONFIG || {}; }
  function myBidder() { var b = hubCfg().bidderNumber; return b ? String(b) : ''; }
  function loggedIn() { return !!(CFG.customerId || hubCfg().customerEmail || myBidder()); }

  var state = null;          // the last snapshot (see lib/timed-close snapshot())
  var offset = 0;            // serverNow − Date.now()
  var socket = null;
  var tickTimer = null;
  var flashTimer = null;
  var flash = null;          // { outcome, price, lotNumber, until }
  var pendingAmount = null;  // the bar's amount waiting on "Confirm"
  var pendingKind = 'bid';   // …and whether it is an exact bid or a max
  var sending = false;
  var dismissedEnd = false;
  var mine = {};             // productId → this bidder's top bid on that lot (exact bids counted)
  var cardPending = {};      // productId → { kind, amount } waiting on the card's Confirm
  var cardSending = {};      // productId → true while that card's bid is in flight

  /* ── REHEARSAL (Simulation Lab, 2026-10-25) ─────────────────────────────────
     Owner: "in rehearsal, any employee or admin logged in to the site should be able to
     bid on the rehearsal auction hub and have their bids get caught in the rehearsal."
     Once a sale turns out to be timed, a SIGNED-IN visitor asks /apps/guac/sim-hub
     (Shopify's App Proxy — the server, not this page, decides who is staff). Staff
     with a timed rehearsal of THIS sale running get a hub pass; from then on every
     request this script makes, and its socket, go to the rehearsal.
     ⚠️ Customers are never swept in: the server answers them { rehearsal: false }, and
     without a pass the run header is ignored.
     ⚠️ A REHEARSAL TAB FAILS CLOSED: every request also carries X-GUAC-Sim-Require, so a
     request the server does not put in the rehearsal (it ended, the pass expired) is
     REFUSED (409 SIM_NOT_TAGGED) — never carried out against the real auction. */
  var SIM = null;            // { runId, token, sessionCode, closingAt, canBid }
  var simChecked = false;
  var simStaffNoRun = false; // staff, but nothing to join yet — ask again each minute
  var simLeaving = false;
  var SIM_NO_KEY = 'mhv_simhub_no';   // per tab: "this visitor is not staff" (never becomes true later)

  function simHeaders(h) {
    h = h || {};
    if (SIM) {
      h['X-GUAC-Sim-Run'] = SIM.runId;
      h['X-GUAC-Sim-Hub'] = SIM.token;
      h['X-GUAC-Sim-Require'] = '1';
    }
    return h;
  }
  function simAsk() {
    if (!CFG.customerId) return Promise.resolve(null);
    try { if (sessionStorage.getItem(SIM_NO_KEY) === CODE) return Promise.resolve(null); } catch (e) { /* private mode */ }
    return fetch('/apps/guac/sim-hub?code=' + encodeURIComponent(CODE), { headers: { 'Accept': 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && !d.rehearsal && !d.staff) { try { sessionStorage.setItem(SIM_NO_KEY, CODE); } catch (e) { /* ok */ } }
        simStaffNoRun = !!(d && d.staff && !d.rehearsal);
        return d;
      })
      .catch(function () { return null; });
  }
  function simBarCss() {
    if (document.getElementById('mhv-timed-sim-css')) return;
    var st = document.createElement('style');
    st.id = 'mhv-timed-sim-css';
    st.textContent = [
      '.mhv-timed-simbar{position:fixed;top:0;left:0;right:0;z-index:2147482500;display:flex;align-items:center;gap:10px;',
      'padding:7px 14px;background:#b8860b;color:#1a1200;font:600 12.5px/1.35 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;',
      'box-shadow:0 2px 8px rgba(0,0,0,.35)}',
      '.mhv-timed-simbar b{font-weight:800}',
      '.mhv-timed-simbar__tag{flex:0 0 auto;font-weight:900;letter-spacing:1px;background:#1a1200;color:#ffd166;border-radius:4px;padding:2px 7px}',
      '.mhv-timed-simbar__txt{flex:1 1 auto}',
      'html.mhv-timed-rehearsal body{padding-top:34px !important}',
      '.mhv-timed-sim-off > :not(.mhv-timed-sim-note){display:none !important}',
      '.mhv-timed-sim-note{font-size:12px;opacity:.8;padding:6px 0}'
    ].join('');
    document.head.appendChild(st);
  }
  function paintSimBar(text) {
    simBarCss();
    var b = document.getElementById('mhv-timed-simbar');
    if (!b) {
      b = document.createElement('div');
      b.id = 'mhv-timed-simbar';
      b.className = 'mhv-timed-simbar';
      b.setAttribute('role', 'status');
      document.body.appendChild(b);
    }
    var when = '';
    try {
      when = SIM && SIM.closingAt ? new Date(SIM.closingAt).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '';
    } catch (e) { when = ''; }
    b.innerHTML = '<span class="mhv-timed-simbar__tag">REHEARSAL</span><span class="mhv-timed-simbar__txt">' + (text ||
      ('Your bids here go into the <b>rehearsal</b> of ' + esc(CODE) + (when ? ' — lots start closing at <b>' + esc(when) + ' ET</b>' : '') +
        '. Customers see the real auction; nothing you do here is real.' +
        (SIM && SIM.canBid === false ? ' Your account has no bidder number, so you can watch but not bid.' : ''))) + '</span>';
  }
  function enterRehearsal(d) {
    SIM = { runId: String(d.runId), token: String(d.token), sessionCode: d.sessionCode, closingAt: d.closingAt || null, canBid: d.canBid !== false };
    window.__mhvSimHub = SIM.runId;   // mhv-sim-notice steps aside (its bar says "REAL AUCTION")
    var real = document.getElementById('mhv-simn-bar');
    if (real) real.remove();
    document.documentElement.classList.add('mhv-timed-rehearsal');
    paintSimBar();
  }
  /* The rehearsal ended (or the pass lapsed and could not be renewed): say so, then
     reload — the page comes back on the real auction, because the server no longer
     hands this visitor a pass. */
  function simLost() {
    if (simLeaving) return;
    simLeaving = true;
    if (SIM) paintSimBar('The rehearsal has ended — reloading the real auction…');
    setTimeout(function () { location.reload(); }, 2500);
  }
  function isSimRefusal(x) { return !!(SIM && x && x.r && x.r.status === 409 && x.d && x.d.error === 'SIM_NOT_TAGGED'); }
  // Renew the pass well inside its two hours; a run that has ended answers rehearsal:false.
  setInterval(function () {
    if (!SIM || simLeaving) return;
    simAsk().then(function (d) {
      if (d && d.rehearsal && String(d.runId) === SIM.runId && d.token) { SIM.token = String(d.token); return; }
      if (d) simLost();   // null = could not ask (offline); try again next time
    });
  }, 10 * 60000);

  var esc = function (s) { return (window.MHV && MHV.escapeHtml) ? MHV.escapeHtml(String(s == null ? '' : s)) : String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  function money(n) {
    var v = Number(n) || 0;
    return '$' + (v % 1 ? v.toFixed(2) : v.toLocaleString('en-US'));
  }
  function serverNow() { return Date.now() + offset; }
  function stamp(sn) { if (sn) offset = Number(sn) - Date.now(); }
  // A catalogue title already starts "Lot 007 - "; the bar prints the number itself.
  function bareTitle(t) { return String(t || '').replace(/^\s*lot\s*#?\s*\d+\s*[-–—:.]?\s*/i, ''); }
  function pad3(n) { var s = String(n == null ? '' : n); return s.length < 3 && /^\d+$/.test(s) ? ('000' + s).slice(-3) : s; }
  function parseAmt(raw) {
    var v = Number(String(raw || '').replace(/[$,\s]/g, ''));
    return isFinite(v) && v > 0 ? v : null;
  }

  // ── the per-lot facts the server serves (snapshot `lots`, timed-bid, timed-lot-update) ──
  function lotInfo(pid) { return (state && state.lots && state.lots[String(pid)]) || null; }
  function updateLotInfo(p) {
    if (!state || !p || !p.productId) return;
    state.lots = state.lots || {};
    var cur = state.lots[String(p.productId)] || {};
    if (p.currentBid != null) cur.currentBid = p.currentBid;
    if (p.bidCount != null) cur.bidCount = p.bidCount;
    cur.leader = p.leader || null;
    if (p.ask != null) { cur.ask = p.ask; cur.ask2 = p.ask2; }
    if (p.reserveMet === true || p.reserveMet === false) {
      cur.reserveMet = p.reserveMet;
      if (window.MHVReserve) window.MHVReserve.set(p.productId, p.reserveMet);
    }
    state.lots[String(p.productId)] = cur;
  }
  /* The reserve pill under the bar's price — '' for a lot with no reserve. */
  function reservePill(met) {
    if (met !== true && met !== false) return '';
    if (window.MHVReserve) return window.MHVReserve.pillHtml(met ? 'met' : 'not_met');
    return '<span class="mhv-reserve' + (met ? ' mhv-reserve--met' : '') + '">' + (met ? 'Reserve met' : 'Reserve not met') + '</span>';
  }

  // ── cards ──────────────────────────────────────────────────────────────────
  function cardFor(pid) {
    pid = String(pid || '');
    if (!pid) return null;
    var el = document.getElementById('current_bid_' + pid) ||
      document.querySelector('.quick-bid-max-bid-interface[data-product-id="' + pid + '"]') ||
      document.getElementById('minimum_bid_block_' + pid);
    if (!el) return null;
    return el.closest('.card-wrapper') || el.closest('.product__info-container') || el.closest('.grid__item') || null;
  }

  function setCardPrice(pid, currentBid, bidCount) {
    pid = String(pid);
    var cur = document.querySelectorAll('[id="current_bid_' + pid + '"]');
    // A card's current bid is always dollars AND cents with commas ($1,500.00) — the
    // way Liquid renders it on first paint and the hub's bid refresh writes it. Using
    // the bar's whole-dollar money() here flipped $375.00 to $375 until the next refresh.
    for (var i = 0; i < cur.length; i++) cur[i].textContent = '$' + (Number(currentBid) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (Number(bidCount) > 0) {
      var info = document.getElementById('bid_info_display_' + pid);
      if (info) info.style.display = '';
      var minBlock = document.getElementById('minimum_bid_block_' + pid);
      var nb = minBlock && minBlock.querySelector('.mhv-lot-stats--nobids .mhv-bid-count-text');
      if (nb) { nb.textContent = bidCount + ' Bid' + (bidCount === 1 ? '' : 's'); nb.style.color = ''; }
      var counts = document.querySelectorAll('[id="absentee_number_of_bids_' + pid + '"]');
      for (var j = 0; j < counts.length; j++) counts[j].textContent = bidCount + ' Bid' + (bidCount === 1 ? '' : 's');
    }
  }

  function markClosing(pid) {
    var prev = document.querySelectorAll('.mhv-timed-closing');
    for (var i = 0; i < prev.length; i++) {
      if (pid && prev[i] === cardFor(pid)) continue;
      prev[i].classList.remove('mhv-timed-closing');
      var n = prev[i].querySelector('.mhv-timed-cardnote'); if (n) n.remove();
    }
    var c = cardFor(pid);
    if (!c) return;
    c.classList.add('mhv-timed-closing');
    if (!c.querySelector('.mhv-timed-cardnote')) {
      // Where the card's bid controls were: say where bidding went.
      var note = document.createElement('div');
      note.className = 'mhv-timed-cardnote';
      note.textContent = '⏱ Closing now — bid in the bar at the bottom of the screen';
      var anchor = document.getElementById('bid_info_display_' + pid) || document.getElementById('minimum_bid_block_' + pid);
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(note, anchor.nextSibling);
      else (c.querySelector('.card__content') || c).appendChild(note);
    }
  }

  function markClosed(pid, info, animate) {
    var c = cardFor(pid);
    if (!c || !info) return;
    c.classList.remove('mhv-timed-closing');
    c.classList.add('mhv-timed-closed');
    c.classList.toggle('mhv-timed-closed--sold', info.outcome === 'sold');
    var tag = c.querySelector('.mhv-timed-result');
    if (!tag) {
      tag = document.createElement('div');
      tag.className = 'mhv-timed-result';
      tag.setAttribute('aria-hidden', 'true');
      var host = c.querySelector('.card__inner') || c.querySelector('.card') || c;
      host.appendChild(tag);
    }
    tag.innerHTML = info.outcome === 'sold'
      ? '<span class="mhv-timed-result__word">SOLD</span>' + (info.price != null ? '<span class="mhv-timed-result__price">' + esc(money(info.price)) + '</span>' : '')
      : '<span class="mhv-timed-result__word">PASSED</span>' +
        (info.reason === 'reserve-not-met' ? '<span class="mhv-timed-result__price">Reserve not met</span>' : '');
    if (animate) {
      c.classList.remove('mhv-timed-flash');
      void c.offsetWidth;   // restart the animation
      c.classList.add('mhv-timed-flash');
    }
  }

  function paintCards() {
    if (!state) return;
    if (state.lots && window.MHVReserve) {
      Object.keys(state.lots).forEach(function (pid) {
        var rm = state.lots[pid] && state.lots[pid].reserveMet;
        if (rm === true || rm === false) window.MHVReserve.set(pid, rm);
      });
    }
    var closed = state.closed || {};
    Object.keys(closed).forEach(function (pid) { markClosed(pid, closed[pid], false); });
    if (state.lot && state.lot.productId) {
      markClosing(state.lot.productId);
      setCardPrice(state.lot.productId, state.lot.currentBid, state.lot.bidCount);
    } else {
      markClosing(null);
    }
    paintAllCardControls();
  }

  // ── each card's own Bid $X + Set a max ────────────────────────────────────
  /* The card's absentee controls (quick bids, "Submit Max Bid") are replaced on every lot
     the server told us about (snapshot `lots`) — never on a lot it did not, so a card the
     map missed keeps working through the old door rather than losing its controls. The
     closing card and closed cards hide the whole box (auction-hub-timed.css). */
  function paintAllCardControls() {
    if (!state || !state.lots) return;
    var boxes = document.querySelectorAll('.quick-bid-max-bid-interface[data-product-id]');
    for (var i = 0; i < boxes.length; i++) paintCardControl(boxes[i]);
  }
  function paintCardsFor(pid) {
    var boxes = document.querySelectorAll('.quick-bid-max-bid-interface[data-product-id="' + String(pid) + '"]');
    for (var i = 0; i < boxes.length; i++) paintCardControl(boxes[i]);
  }

  function paintCardControl(box) {
    var pid = String(box.getAttribute('data-product-id') || '');
    var info = lotInfo(pid);
    if (!info) {
      // In a rehearsal a card the rehearsal does not know keeps NO controls: its old
      // absentee door would post to the real auction from a page wearing the
      // REHEARSAL bar.
      if (SIM && !box.classList.contains('mhv-timed-sim-off')) {
        box.classList.add('mhv-timed-sim-off');
        var note = document.createElement('div');
        note.className = 'mhv-timed-sim-note';
        note.textContent = 'Not part of the rehearsal.';
        box.appendChild(note);
      }
      return;
    }
    box.classList.add('mhv-timed-ctl');
    // The card's old "You are the high bidder!" / "Your Max Bid:" lines describe the
    // absentee door and would contradict this one — the control says it now.
    ['bidder_status_message_' + pid, 'user_max_bid_display_' + pid, 'user_max_bid_display_info_' + pid].forEach(function (id) {
      var els = document.querySelectorAll('[id="' + id + '"]');
      for (var k = 0; k < els.length; k++) {
        var hide = id.indexOf('user_max_bid_display') === 0 ? els[k].parentNode : els[k];
        if (hide && hide.style) hide.style.display = 'none';
      }
    });
    var ctl = box.querySelector('.mhv-timed-card');
    if (!ctl) {
      ctl = document.createElement('div');
      ctl.className = 'mhv-timed-card';
      ctl.setAttribute('data-pid', pid);
      box.insertBefore(ctl, box.firstChild);
    }
    var me = myBidder();
    var leading = !!(me && info.leader && String(info.leader) === me);
    var top = mine[pid] || null;
    var pend = cardPending[pid] || null;
    var busy = !!cardSending[pid];
    var key = [pid, info.ask, info.leader, info.bidCount, top, pend ? pend.kind + pend.amount : '', busy, me, loggedIn(), info.reserveMet].join('|');
    if (ctl.getAttribute('data-key') === key) return;
    ctl.setAttribute('data-key', key);
    // The price from the bid state itself — the card's Liquid figure is the Shopify
    // mirror, which a bid on the closing lot never updates.
    if (Number(info.bidCount) > 0) setCardPrice(pid, info.currentBid, info.bidCount);

    // ⚠️ Somebody else's bid repaints this — keep what this bidder was typing.
    var oldInp = ctl.querySelector('input');
    var typed = oldInp ? oldInp.value : '';
    var hadFocus = !!(oldInp && document.activeElement === oldInp);
    var oldMsg = ctl.querySelector('.mhv-timed-card__msg');
    var msgHtml = oldMsg ? oldMsg.outerHTML : '<div class="mhv-timed-card__msg" role="alert"></div>';

    var status = '';
    if (leading) status = '<span class="mhv-timed-lead">✓ You’re the high bidder</span>' + (top ? ' <span class="mhv-timed-card__mine">· your max ' + esc(money(top)) + '</span>' : '');
    else if (top) status = '<span class="mhv-timed-out">You’ve been outbid</span> <span class="mhv-timed-card__mine">· your top bid ' + esc(money(top)) + '</span>';

    var body;
    if (!me) {
      body = '<button type="button" class="mhv-timed-btn" data-tact="register">Register to bid</button>';
    } else if (pend) {
      body = '<span class="mhv-timed-confirm-q">' + (pend.kind === 'max'
          ? (leading ? 'Raise your maximum to <strong>' : 'Set your maximum to <strong>') + esc(money(pend.amount)) + '</strong>?'
          : 'Bid <strong>' + esc(money(pend.amount)) + '</strong>?') + '</span>' +
        '<button type="button" class="mhv-timed-btn mhv-timed-btn--go" data-tact="confirm"' + (busy ? ' disabled' : '') + '>' + (busy ? 'Bidding…' : 'Confirm') + '</button>' +
        '<button type="button" class="mhv-timed-btn mhv-timed-btn--ghost" data-tact="cancel"' + (busy ? ' disabled' : '') + '>Cancel</button>';
    } else {
      body = (leading ? '' : '<button type="button" class="mhv-timed-btn mhv-timed-btn--bid" data-tact="bid" data-amount="' + esc(info.ask) + '">Bid ' + esc(money(info.ask)) + '</button>') +
        '<form class="mhv-timed-max" novalidate>' +
          '<label class="mhv-timed-sr" for="mhv-tmax-' + esc(pid) + '">Your maximum bid</label>' +
          '<span class="mhv-timed-own__dollar">$</span>' +
          '<input id="mhv-tmax-' + esc(pid) + '" type="text" inputmode="decimal" autocomplete="off" placeholder="' + (leading ? 'New max' : 'Your max') + '">' +
          '<button type="submit" class="mhv-timed-btn mhv-timed-btn--ghost">' + (leading ? 'Raise max' : 'Set a max') + '</button>' +
        '</form>';
    }
    ctl.innerHTML =
      (status ? '<div class="mhv-timed-card__status">' + status + '</div>' : '') +
      '<div class="mhv-timed-card__row">' + body + '</div>' +
      msgHtml +
      (me && !pend ? '<div class="mhv-timed-card__help">' + (leading
        ? (info.reserveMet === false
          ? 'You’re winning, but the reserve hasn’t been met. Until a maximum passes it the price shows yours in full, so raising your max raises the price (to the reserve at most).'
          : 'You’re winning. Raise your max any time — the price only goes up when someone bids against you.')
        : 'Bid the next amount, or set a max and we’ll bid for you, one step at a time, up to it.') + '</div>' : '');
    var inp = ctl.querySelector('input');
    if (inp && typed) inp.value = typed;
    if (inp && hadFocus) { inp.focus(); try { inp.setSelectionRange(typed.length, typed.length); } catch (e) { /* number keypads */ } }
  }

  function cardMsg(pid, text, kind) {
    var boxes = document.querySelectorAll('.mhv-timed-card[data-pid="' + String(pid) + '"] .mhv-timed-card__msg');
    for (var i = 0; i < boxes.length; i++) {
      boxes[i].textContent = text || '';
      boxes[i].className = 'mhv-timed-card__msg' + (kind ? ' mhv-timed-card__msg--' + kind : '');
    }
    clearTimeout(cardMsg['_t' + pid]);
    if (text) cardMsg['_t' + pid] = setTimeout(function () { cardMsg(pid, ''); }, 6000);
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('.mhv-timed-card [data-tact]') : null;
    if (!b) return;
    e.preventDefault();
    var ctl = b.closest('.mhv-timed-card');
    var pid = ctl.getAttribute('data-pid');
    var act = b.getAttribute('data-tact');
    if (act === 'register') { openRegister(null); return; }
    if (act === 'bid') { cardPending[pid] = { kind: 'bid', amount: Number(b.getAttribute('data-amount')) }; paintCardsFor(pid); return; }
    if (act === 'cancel') { delete cardPending[pid]; paintCardsFor(pid); return; }
    if (act === 'confirm') {
      var p = cardPending[pid];
      if (!p || cardSending[pid]) return;
      cardSending[pid] = true; paintCardsFor(pid);
      sendBid(pid, p.kind, p.amount, function (ok, text, tone) {
        cardSending[pid] = false;
        delete cardPending[pid];
        paintCardsFor(pid);
        cardMsg(pid, text, tone);
      });
    }
  });
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f.classList || !f.classList.contains('mhv-timed-max')) return;
    e.preventDefault();
    var ctl = f.closest('.mhv-timed-card');
    var pid = ctl.getAttribute('data-pid');
    var amt = parseAmt((f.querySelector('input') || {}).value);
    if (amt == null) { cardMsg(pid, 'Enter your maximum bid.', 'err'); return; }
    var info = lotInfo(pid) || {};
    var leading = info.leader && String(info.leader) === myBidder();
    if (leading && mine[pid] && amt <= mine[pid]) { cardMsg(pid, 'Your maximum is already ' + money(mine[pid]) + ' — enter more to raise it.', 'err'); return; }
    if (!leading && Number(info.bidCount) > 0 && amt <= Number(info.currentBid)) { cardMsg(pid, 'Your maximum has to be more than the current bid of ' + money(info.currentBid) + '.', 'err'); return; }
    cardPending[pid] = { kind: 'max', amount: Math.round(amt * 100) / 100 };
    paintCardsFor(pid);
  });

  // ── the bar ───────────────────────────────────────────────────────────────
  var bar = null;
  function ensureBar() {
    if (bar) return bar;
    bar = document.createElement('section');
    bar.className = 'mhv-timed-bar';
    bar.setAttribute('aria-label', 'Lots closing now');
    bar.innerHTML =
      '<div class="mhv-timed-bar__in">' +
        '<div class="mhv-timed-bar__lot"></div>' +
        '<div class="mhv-timed-bar__clockwrap"><div class="mhv-timed-bar__clock" aria-live="off"></div><div class="mhv-timed-bar__pops" aria-hidden="true"></div></div>' +
        '<div class="mhv-timed-bar__price"></div>' +
        '<div class="mhv-timed-bar__status" role="status" aria-live="polite"></div>' +
        '<div class="mhv-timed-bar__actions"></div>' +
        '<div class="mhv-timed-bar__msg" role="alert"></div>' +
      '</div>';
    document.body.appendChild(bar);
    document.documentElement.classList.add('mhv-timed-has-bar');
    // The page keeps its bottom clear of the bar, whatever height it wraps to.
    var setH = function () { if (bar) document.documentElement.style.setProperty('--mhv-timed-bar-h', bar.offsetHeight + 'px'); };
    setH();
    if (window.ResizeObserver) new ResizeObserver(setH).observe(bar);
    bar.addEventListener('click', onBarClick);
    bar.addEventListener('submit', onBarSubmit);
    return bar;
  }
  function removeBar() {
    if (!bar) return;
    bar.remove(); bar = null;
    document.documentElement.classList.remove('mhv-timed-has-bar');
  }
  function q(sel) { return bar ? bar.querySelector(sel) : null; }

  function clockText(ms) {
    if (ms <= 0) return '0:00';
    var s = Math.ceil(ms / 1000);
    return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
  }
  function longCountdown(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (d > 0) return d + 'd ' + h + 'h ' + m + 'm';
    if (h > 0) return h + 'h ' + m + 'm ' + ('0' + sec).slice(-2) + 's';
    return m + 'm ' + ('0' + sec).slice(-2) + 's';
  }

  function render() {
    if (!state || !state.timed) { removeBar(); return; }
    var lot = state.lot;
    var now = serverNow();

    // Finished — say so once, and let it be dismissed.
    if (state.status === 'done' || (state.ended && !lot)) {
      if (dismissedEnd) { removeBar(); return; }
      ensureBar();
      bar.className = 'mhv-timed-bar mhv-timed-bar--done';
      q('.mhv-timed-bar__lot').innerHTML = '<strong>This auction has closed.</strong> Thank you for bidding!';
      q('.mhv-timed-bar__clock').textContent = '';
      q('.mhv-timed-bar__price').textContent = '';
      q('.mhv-timed-bar__status').textContent = '';
      q('.mhv-timed-bar__actions').innerHTML = '<button type="button" class="mhv-timed-btn mhv-timed-btn--ghost" data-act="dismiss">Close</button>';
      return;
    }

    // The flash between lots: SOLD $x / PASSED.
    if (flash && flash.until > Date.now()) {
      ensureBar();
      bar.className = 'mhv-timed-bar mhv-timed-bar--flash mhv-timed-bar--' + flash.outcome;
      q('.mhv-timed-bar__lot').innerHTML = 'Lot ' + esc(pad3(flash.lotNumber));
      q('.mhv-timed-bar__clock').textContent = flash.outcome === 'sold' ? 'SOLD' : 'PASSED';
      q('.mhv-timed-bar__price').textContent = flash.outcome === 'sold' && flash.price != null ? money(flash.price) : '';
      var mineWon = flash.outcome === 'sold' && flash.leader && myBidder() && String(flash.leader) === myBidder();
      var passedReserve = flash.outcome === 'passed' && flash.reason === 'reserve-not-met';
      q('.mhv-timed-bar__status').innerHTML = mineWon ? '<span class="mhv-timed-won">You won it!</span>'
        : passedReserve ? '<strong>' + esc(window.MHVReserve ? window.MHVReserve.passedText(flash.reason) : 'Passed — reserve not met') + '</strong> · Next lot coming up…'
          : 'Next lot coming up…';
      q('.mhv-timed-bar__actions').innerHTML = '';
      return;
    }

    // Not started yet: a countdown to the first clock (only once it is close).
    if (!lot) {
      var startAt = state.closingStartsAt;
      if (state.status === 'paused') {
        ensureBar();
        bar.className = 'mhv-timed-bar mhv-timed-bar--wait';
        q('.mhv-timed-bar__lot').innerHTML = '<strong>Closing is paused</strong> — it will resume shortly.';
        q('.mhv-timed-bar__clock').textContent = '';
        q('.mhv-timed-bar__price').textContent = '';
        q('.mhv-timed-bar__status').textContent = '';
        q('.mhv-timed-bar__actions').innerHTML = '';
        return;
      }
      if (state.status === 'running') {
        ensureBar();
        bar.className = 'mhv-timed-bar mhv-timed-bar--wait';
        q('.mhv-timed-bar__lot').innerHTML = '<strong>Next lot coming up…</strong>';
        ['.mhv-timed-bar__clock', '.mhv-timed-bar__price', '.mhv-timed-bar__status'].forEach(function (s) { q(s).textContent = ''; });
        q('.mhv-timed-bar__actions').innerHTML = '';
        return;
      }
      if (startAt && startAt > now && startAt - now < 24 * 3600000) {
        ensureBar();
        bar.className = 'mhv-timed-bar mhv-timed-bar--soon';
        q('.mhv-timed-bar__lot').innerHTML = '<strong>Lots start closing in</strong>';
        q('.mhv-timed-bar__clock').textContent = longCountdown(startAt - now);
        q('.mhv-timed-bar__price').textContent = '';
        q('.mhv-timed-bar__status').textContent = 'One lot at a time, one minute each — every bid adds up to 15 seconds. Bid or set a max on any lot now.';
        q('.mhv-timed-bar__actions').innerHTML = '';
        return;
      }
      removeBar();
      return;
    }

    ensureBar();
    var paused = state.status === 'paused';
    var remaining = paused ? (lot.pausedRemainingMs || 0) : (lot.endsAt || 0) - now;
    var me = myBidder();
    var leading = !!(me && lot.leader && String(lot.leader) === me);
    var outbid = !!(me && !leading && (lot.myBid || mine[String(lot.productId)]));
    bar.className = 'mhv-timed-bar mhv-timed-bar--live' +
      (remaining < 10000 && !paused ? ' mhv-timed-bar--urgent' : '') +
      (leading ? ' mhv-timed-bar--leading' : '') + (outbid ? ' mhv-timed-bar--outbid' : '') +
      (paused ? ' mhv-timed-bar--paused' : '');

    var href = lot.handle ? '/products/' + encodeURIComponent(lot.handle) + '?collection=' + encodeURIComponent(CODE) : '';
    var onPage = !!cardFor(lot.productId);
    var lotKey = lot.productId + '|' + lot.bidCount + '|' + lot.leader + '|' + leading + '|' + outbid + '|' + paused + '|' + lot.ask + '|' + lot.reserveMet + '|' + lot.currentBid;
    if (bar.getAttribute('data-key') !== lotKey) {
      bar.setAttribute('data-key', lotKey);
      q('.mhv-timed-bar__lot').innerHTML =
        '<span class="mhv-timed-bar__kicker">' + (paused ? 'Paused' : 'Now closing') + '</span>' +
        '<span class="mhv-timed-bar__title">' +
          (onPage ? '<button type="button" class="mhv-timed-link" data-act="show">Lot ' + esc(pad3(lot.lotNumber)) + '</button>'
            : (href ? '<a class="mhv-timed-link" href="' + esc(href) + '">Lot ' + esc(pad3(lot.lotNumber)) + '</a>' : 'Lot ' + esc(pad3(lot.lotNumber)))) +
          ' <span class="mhv-timed-bar__name">' + esc(bareTitle(lot.title)) + '</span>' +
        '</span>';
      q('.mhv-timed-bar__price').innerHTML = (lot.bidCount > 0
        ? '<span class="mhv-timed-bar__amt">' + esc(money(lot.currentBid)) + '</span><span class="mhv-timed-bar__count">' + lot.bidCount + ' bid' + (lot.bidCount === 1 ? '' : 's') + '</span>'
        : '<span class="mhv-timed-bar__amt mhv-timed-bar__amt--none">No bids</span>' +
          (lot.minimumBid ? '<span class="mhv-timed-bar__count">opens at ' + esc(money(lot.minimumBid)) + '</span>' : '')) +
        reservePill(lot.reserveMet);
      var status = '';
      if (leading) status = '<span class="mhv-timed-lead">✓ You’re the high bidder</span>';
      else if (outbid) status = '<span class="mhv-timed-out">You’ve been outbid</span>';
      else if (lot.leader) status = 'High bidder #' + esc(lot.leader);
      q('.mhv-timed-bar__status').innerHTML = status;
      renderActions();
    }
    q('.mhv-timed-bar__clock').textContent = paused ? 'PAUSED' : clockText(remaining);
  }

  function renderActions() {
    var box = q('.mhv-timed-bar__actions');
    if (!box || !state || !state.lot) return;
    var lot = state.lot;
    if (state.status === 'paused') { box.innerHTML = ''; return; }
    if (!loggedIn()) {
      box.innerHTML = '<a class="mhv-timed-btn" href="/account/login?checkout_url=' + encodeURIComponent(location.pathname + location.search) + '">Log in to bid</a>';
      return;
    }
    if (!myBidder()) {
      box.innerHTML = '<button type="button" class="mhv-timed-btn" data-act="register">Register to bid</button>';
      return;
    }
    if (pendingAmount != null) {
      box.innerHTML =
        '<span class="mhv-timed-confirm-q">' + (pendingKind === 'max' ? 'Set your maximum to <strong>' : 'Bid <strong>') + esc(money(pendingAmount)) + '</strong>?</span>' +
        '<button type="button" class="mhv-timed-btn mhv-timed-btn--go" data-act="confirm"' + (sending ? ' disabled' : '') + '>' + (sending ? 'Bidding…' : 'Confirm') + '</button>' +
        '<button type="button" class="mhv-timed-btn mhv-timed-btn--ghost" data-act="cancel"' + (sending ? ' disabled' : '') + '>Cancel</button>';
      return;
    }
    var leading = lot.leader && String(lot.leader) === myBidder();
    // ⚠️ Somebody else's bid repaints this — keep what this bidder was typing.
    var oldInp = box.querySelector('#mhv-timed-amt');
    var typed = oldInp ? oldInp.value : '';
    var hadFocus = !!(oldInp && document.activeElement === oldInp);
    /* The leader is offered only "Raise max": their own bid never raises the price
       (lib/timed-close — the server would only raise their maximum anyway). */
    box.innerHTML = leading
      ? '<form class="mhv-timed-own" novalidate data-kind="max"><label class="mhv-timed-sr" for="mhv-timed-amt">Your new maximum</label>' +
          '<span class="mhv-timed-own__dollar">$</span><input id="mhv-timed-amt" type="text" inputmode="decimal" autocomplete="off" placeholder="New max">' +
          '<button type="submit" class="mhv-timed-btn mhv-timed-btn--ghost">Raise max</button></form>'
      : '<button type="button" class="mhv-timed-btn mhv-timed-btn--bid" data-act="ask" data-amount="' + lot.ask + '">Bid ' + esc(money(lot.ask)) + '</button>' +
        (lot.ask2 && lot.ask2 > lot.ask ? '<button type="button" class="mhv-timed-btn mhv-timed-btn--bid2" data-act="ask" data-amount="' + lot.ask2 + '">Bid ' + esc(money(lot.ask2)) + '</button>' : '') +
        '<form class="mhv-timed-own" novalidate data-kind="bid"><label class="mhv-timed-sr" for="mhv-timed-amt">Your bid or maximum</label>' +
          '<span class="mhv-timed-own__dollar">$</span><input id="mhv-timed-amt" type="text" inputmode="decimal" autocomplete="off" placeholder="' + esc(String(lot.ask)) + '">' +
          '<button type="submit" class="mhv-timed-btn mhv-timed-btn--ghost">Bid</button>' +
          '<button type="button" class="mhv-timed-btn mhv-timed-btn--ghost" data-act="own-max">Set a max</button></form>';
    var inp = box.querySelector('#mhv-timed-amt');
    if (inp && typed) inp.value = typed;
    if (inp && hadFocus) { inp.focus(); try { inp.setSelectionRange(typed.length, typed.length); } catch (e) { /* number keypads */ } }
  }

  function showMsg(text, kind) {
    var m = q('.mhv-timed-bar__msg');
    if (!m) return;
    m.textContent = text || '';
    m.className = 'mhv-timed-bar__msg' + (kind ? ' mhv-timed-bar__msg--' + kind : '');
    clearTimeout(showMsg._t);
    if (text) showMsg._t = setTimeout(function () { if (m) m.textContent = ''; }, 5000);
  }

  function pop(addedMs) {
    var host = q('.mhv-timed-bar__pops');
    if (!host || !(addedMs > 0)) return;
    var s = document.createElement('span');
    s.className = 'mhv-timed-pop';
    s.textContent = '+' + Math.round(addedMs / 1000) + 's';
    host.appendChild(s);
    setTimeout(function () { s.remove(); }, 1400);
    var c = state && state.lot && cardFor(state.lot.productId);
    if (c) {
      var cp = document.createElement('span');
      cp.className = 'mhv-timed-pop mhv-timed-pop--card';
      cp.textContent = s.textContent;
      (c.querySelector('.card__inner') || c).appendChild(cp);
      setTimeout(function () { cp.remove(); }, 1400);
    }
  }

  // ── actions ───────────────────────────────────────────────────────────────
  function onBarClick(e) {
    var b = e.target.closest('[data-act]');
    if (!b || !bar.contains(b)) return;
    var act = b.getAttribute('data-act');
    if (act === 'dismiss') { dismissedEnd = true; removeBar(); return; }
    if (act === 'show') {
      var c = state && state.lot && cardFor(state.lot.productId);
      if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (act === 'register') { openRegister(null); return; }
    if (act === 'ask') { pendingKind = 'bid'; pendingAmount = Number(b.getAttribute('data-amount')); renderActions(); return; }
    if (act === 'own-max') { takeTyped(b.closest('form'), 'max'); return; }
    if (act === 'cancel') { pendingAmount = null; renderActions(); return; }
    if (act === 'confirm') { placeBid(pendingAmount, pendingKind); return; }
  }
  function onBarSubmit(e) {
    if (!e.target.classList.contains('mhv-timed-own')) return;
    e.preventDefault();
    takeTyped(e.target, e.target.getAttribute('data-kind') === 'max' ? 'max' : 'bid');
  }
  function takeTyped(form, kind) {
    var inp = form && form.querySelector('input');
    var amt = parseAmt(inp && inp.value);
    if (amt == null) { showMsg(kind === 'max' ? 'Enter your maximum bid.' : 'Enter a bid amount.', 'err'); return; }
    var lot = state && state.lot;
    var leading = lot && lot.leader && String(lot.leader) === myBidder();
    if (lot && !leading && kind === 'bid' && amt < lot.ask) { showMsg('The next bid is at least ' + money(lot.ask) + '.', 'err'); return; }
    if (lot && !leading && kind === 'max' && Number(lot.bidCount) > 0 && amt <= Number(lot.currentBid)) { showMsg('Your maximum has to be more than ' + money(lot.currentBid) + '.', 'err'); return; }
    pendingKind = kind;
    pendingAmount = kind === 'max' ? Math.round(amt * 100) / 100 : Math.floor(amt * 2) / 2;
    renderActions();
  }

  function openRegister(retry) {
    // The registration modal talks to the REAL auction (and vaults a card through the
    // live Square rail). A rehearsal registers staff on its own — never from here.
    if (SIM) { showMsg('Registering is not part of the rehearsal — reload the page; if this keeps happening, the rehearsal could not register you.', 'err'); return; }
    if (!window.MHVAuctionRegister) { showMsg('Please register for this auction to bid.', 'err'); return; }
    window.MHVAuctionRegister.open({ auctionCode: CODE, onRegistered: function () {
      if (typeof retry === 'function') retry();
    } });
  }

  /**
   * THE ONE BID REQUEST — the bar and every card send through here.
   * done(ok, message, tone) — the message is what to show the bidder.
   */
  function sendBid(pid, kind, amount, done, simRetried) {
    var f = window.mhvFetch || window.fetch;
    if (SIM && SIM.canBid === false) { done(false, 'Your account has no bidder number, so it cannot bid in the rehearsal.', 'err'); return; }
    f(SERVER + '/api/timed/bid', {
      method: 'POST',
      headers: simHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json' }),
      body: JSON.stringify({ session_code: CODE, product_id: pid, amount: amount, kind: kind === 'max' ? 'max' : 'bid', bidder_number: myBidder() })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (d) { return { r: r, d: d }; });
    }).then(function (x) {
      var d = x.d || {};
      if (isSimRefusal(x)) { done(false, 'The rehearsal has ended — this bid was NOT placed.', 'err'); simLost(); return; }
      // In a rehearsal a 401 usually means this visitor's customer key was made after
      // the rehearsal copied their account in. Asking for the pass again re-copies it;
      // then the bid is tried once more.
      if (SIM && x.r.status === 401 && !simRetried) {
        simAsk().then(function (d) {
          if (d && d.rehearsal && String(d.runId) === SIM.runId && d.token) SIM.token = String(d.token);
          sendBid(pid, kind, amount, done, true);
        });
        return;
      }
      if (x.r.ok && d.ok) {
        var top = Math.max(Number(mine[String(pid)]) || 0, Number(d.yourMax) || 0, Number(amount) || 0);
        mine[String(pid)] = top;
        if (d.state) {
          updateLotInfo(Object.assign({}, d.state, { productId: pid }));
          if (state && state.lot && String(state.lot.productId) === String(pid)) { state.lot.myBid = amount; if (!d.selfRaise || d.moved) applyBid(d.state); }
          else setCardPrice(pid, d.state.currentBid, d.state.bidCount);
        }
        paintCardsFor(pid);
        var text;
        if (d.selfRaise && d.moved && d.reserveMet === false) text = 'Your maximum is now ' + money(amount) + '. The reserve hasn’t been met, so the price shows your maximum in full.';
        else if (d.selfRaise && d.moved) text = 'Your maximum is now ' + money(amount) + ' — it passed the reserve, so the price rose to the reserve.';
        else if (d.selfRaise) text = 'Your maximum is now ' + money(amount) + ' — the price stays where it is until someone bids against you.';
        else if (d.isHigh && d.kind === 'max') text = 'Your maximum of ' + money(amount) + ' is set — you’re the high bidder.';
        else if (d.isHigh) text = 'Bid placed — you’re the high bidder.';
        else text = 'Bid placed, but another bidder’s maximum is higher.';
        done(true, text, d.isHigh ? 'ok' : 'warn');
        return;
      }
      // (In a rehearsal neither gate opens a modal: both would act on the REAL account.)
      if (d.error === 'PHONE_VERIFICATION_REQUIRED' && window.MHVVerify && !SIM) {
        done(false, '', null);
        window.MHVVerify.open({ message: 'Please verify your phone number to place your bid.', onVerified: function () { sendBid(pid, kind, amount, done); } });
        return;
      }
      if (d.error === 'AUCTION_REGISTRATION_REQUIRED' && !SIM) {
        done(false, '', null);
        openRegister(function () { sendBid(pid, kind, amount, done); });
        return;
      }
      done(false, d.message || d.error || 'Your bid could not be placed.', 'err');
      if (d.error === 'REJECTED' || d.error === 'LOT_CLOSED' || d.error === 'NOT_CURRENT') refresh();
    }).catch(function () {
      done(false, 'Connection problem — your bid may not have been placed. Check the price and try again.', 'err');
      refresh();
    });
  }

  function placeBid(amount, kind) {
    if (sending || amount == null || !state || !state.lot) return;
    var lot = state.lot;
    sending = true; renderActions();
    sendBid(lot.productId, kind, amount, function (ok, text, tone) {
      sending = false;
      pendingAmount = null;
      if (bar) bar.removeAttribute('data-key');
      render();
      renderActions();
      if (text) showMsg(text, tone);
    });
  }

  // ── server messages ───────────────────────────────────────────────────────
  function applyState(snap) {
    if (!snap || String(snap.sessionCode) !== CODE) return;
    stamp(snap.serverNow);
    var keepMine = state && state.lot && snap.lot && String(state.lot.productId) === String(snap.lot.productId) ? state.lot.myBid : null;
    // A full snapshot from a broadcast does not carry closingStartsAt or the lots map;
    // keep the ones we have.
    if (state && snap.closingStartsAt === undefined) snap.closingStartsAt = state.closingStartsAt;
    if (state && snap.ended === undefined) snap.ended = state.ended;
    if (state && snap.lots === undefined) snap.lots = state.lots;
    state = snap;
    if (state.lot) updateLotInfo(state.lot);
    if (state.lot && keepMine != null) state.lot.myBid = keepMine;
    if (state.lot && (!state.lot.myBid) && myBidder() && state.lot.leader === myBidder()) state.lot.myBid = state.lot.currentBid;
    if (state.lots) document.documentElement.classList.add('mhv-timed-sale');
    if (bar) bar.removeAttribute('data-key');
    paintCards();
    render();
    startTicking();
  }

  function applyBid(p) {
    updateLotInfo(p);
    paintCardsFor(p.productId);
    if (!state || !state.lot || String(p.productId) !== String(state.lot.productId)) return;
    stamp(p.serverNow);
    var wasLeader = state.lot.leader;
    if (p.endsAt) state.lot.endsAt = p.endsAt;
    state.lot.currentBid = p.currentBid;
    state.lot.bidCount = p.bidCount;
    state.lot.leader = p.leader;
    state.lot.ask = p.ask; state.lot.ask2 = p.ask2;
    if (p.reserveMet === true || p.reserveMet === false) state.lot.reserveMet = p.reserveMet;
    if (myBidder() && p.leader === myBidder()) state.lot.myBid = p.currentBid;
    // A stale pending confirm for an exact amount the price has passed is no longer a bid.
    if (pendingAmount != null && pendingKind === 'bid' && pendingAmount < p.ask) pendingAmount = null;
    setCardPrice(p.productId, p.currentBid, p.bidCount);
    if (bar) bar.removeAttribute('data-key');
    render();
    pop(p.addedMs);
    if (myBidder() && wasLeader === myBidder() && p.leader !== myBidder()) {
      var b = q('.mhv-timed-bar__status');
      if (b) { b.classList.remove('mhv-timed-shake'); void b.offsetWidth; b.classList.add('mhv-timed-shake'); }
    }
  }

  function applyClosed(p) {
    if (String(p.sessionCode) !== CODE) return;
    stamp(p.serverNow);
    var info = { outcome: p.outcome, price: p.price, leader: p.leader, lotNumber: p.lotNumber, reason: p.reason || null };
    if (state) { state.closed = state.closed || {}; state.closed[p.productId] = info; if (state.lot && String(state.lot.productId) === String(p.productId)) state.lot = null; }
    pendingAmount = null;
    markClosed(p.productId, info, true);
    flash = { outcome: p.outcome, price: p.price, leader: p.leader, lotNumber: p.lotNumber, reason: p.reason || null, until: Date.now() + 1800 };
    clearTimeout(flashTimer);
    flashTimer = setTimeout(function () { flash = null; render(); }, 1850);
    if (bar) bar.removeAttribute('data-key');
    render();
  }

  // ── plumbing ──────────────────────────────────────────────────────────────
  function startTicking() {
    if (tickTimer) return;
    tickTimer = setInterval(function () { if (!document.hidden) render(); }, 200);
  }

  /* This bidder's own top bid on every lot — exact bids counted (GET …/mine). Needs the
     customer key, which mhvFetch adds; quietly nothing without one. */
  function loadMine() {
    var me = myBidder();
    if (!me || !window.mhvFetch) return;
    window.mhvFetch(SERVER + '/api/timed/' + CODE + '/mine/' + encodeURIComponent(me), { method: 'GET', headers: simHeaders({}) })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.mine) return;
        Object.keys(d.mine).forEach(function (pid) { mine[pid] = Math.max(Number(mine[pid]) || 0, Number(d.mine[pid]) || 0); });
        paintAllCardControls();
        if (bar) bar.removeAttribute('data-key');
        render();
      })
      .catch(function () { /* a card simply shows no "your top bid" */ });
  }

  function refresh() {
    if (simLeaving) return Promise.resolve();
    return fetch(SERVER + '/api/timed/' + CODE + '/state', { cache: 'no-store', headers: simHeaders({}) })
      .then(function (r) { return r.json().catch(function () { return null; }).then(function (d) { return { r: r, d: d }; }); })
      .then(function (x) {
        if (isSimRefusal(x)) { simLost(); return; }
        var snap = x.r.ok ? x.d : null;
        if (!snap) return;
        if (!snap.timed) { state = null; removeBar(); return; }
        // First sight of a timed sale: is this a staff member with a rehearsal of it
        // running? Asked BEFORE anything is painted or connected, so a rehearsal tab
        // never shows the real clock first.
        if (!simChecked) {
          simChecked = true;
          return simAsk().then(function (d) {
            if (d && d.rehearsal && d.runId && d.token) { enterRehearsal(d); return refresh(); }
            applyState(snap); loadMine(); connect();
          });
        }
        applyState(snap);
        loadMine();
        connect();
      })
      .catch(function () { /* the socket's reconnect asks again */ });
  }

  function connect() {
    if (socket) return;
    function go() {
      try {
        // In a rehearsal the socket joins the run's room (the pass names the run), so the
        // clock it follows is the rehearsal's. Function form: a reconnect sends the
        // CURRENT pass, not the one this page started with.
        var opts = { transports: ['websocket', 'polling'], reconnection: true };
        if (SIM) opts.auth = function (cb) { cb(SIM ? { simRunId: SIM.runId, simHubToken: SIM.token } : {}); };
        socket = window.io(SERVER, opts);
        socket.on('timed-state', applyState);
        socket.on('timed-bid', function (p) { if (p && String(p.sessionCode) === CODE) applyBid(p); });
        socket.on('timed-lot-closed', applyClosed);
        socket.on('timed-lot-update', function (p) {
          if (!p || String(p.sessionCode) !== CODE) return;
          updateLotInfo(p);
          setCardPrice(p.productId, p.currentBid, p.bidCount);
          paintCardsFor(p.productId);
        });
        socket.on('timed-sale-ended', function (p) { if (p && String(p.sessionCode) === CODE) refresh(); });
        // Anything missed while the connection was down is read back in full.
        socket.on('connect', function () { if (state) refresh(); });
      } catch (e) { socket = null; }
    }
    if (window.io) return go();
    var sc = document.createElement('script');
    sc.src = SERVER + '/socket.io/socket.io.js';
    sc.onload = function () { if (window.io) go(); };
    document.head.appendChild(sc);
  }

  /* Cards that arrive later (infinite scroll, a filter, the lightbox) get their controls.
     ⚠️ Only an ADDED card counts — the bar's clock repaints five times a second, and
     reacting to every mutation would repaint every card forever. */
  if (window.MutationObserver) {
    var paintSoon = null;
    new MutationObserver(function (muts) {
      if (!state || !state.lots || paintSoon) return;
      var found = false;
      for (var i = 0; i < muts.length && !found; i++) {
        var added = muts[i].addedNodes || [];
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 && (n.matches('.quick-bid-max-bid-interface') || n.querySelector('.quick-bid-max-bid-interface'))) { found = true; break; }
        }
      }
      if (!found) return;
      paintSoon = setTimeout(function () { paintSoon = null; paintCards(); }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('visibilitychange', function () { if (!document.hidden && state) refresh(); });
  // Before closing starts, a slow poll is the fallback for a missed broadcast.
  setInterval(function () {
    if (document.hidden || !state) return;
    if (state.status !== 'running' && state.status !== 'paused') refresh();
    // Staff on the hub before a rehearsal started: join it once it does (a reload, so
    // nothing real that was already painted or connected carries over).
    if (!SIM && simStaffNoRun) simAsk().then(function (d) { if (d && d.rehearsal) location.reload(); });
  }, 60000);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
